import React from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

/**
 * MathRenderer - Renders text with LaTeX math expressions
 * 
 * Supports:
 * - Inline math: \( ... \) or $ ... $
 * - Block math: \[ ... \] or $$ ... $$
 * - Auto-detect bare LaTeX: CO_{2}, x^{2}, \frac{1}{2}, etc.
 * - Fractions: \frac{1}{2}
 * - Exponents: x^2, x^{10}
 * - Subscripts: H_2O, x_1
 * - Square roots: \sqrt{4}
 * - Greek letters: \pi, \alpha, \beta
 * - Chemical formulas: handled via subscripts
 */

// Patterns that indicate LaTeX content (even without delimiters)
const LATEX_PATTERNS = [
  /\\frac\{[^}]+\}\{[^}]+\}/,  // \frac{a}{b}
  /\\sqrt\{[^}]+\}/,            // \sqrt{x}
  /\\sqrt\[[^\]]+\]\{[^}]+\}/,  // \sqrt[n]{x}
  /[A-Za-z0-9]+_\{[^}]+\}/,     // x_{subscript}
  /[A-Za-z0-9]+_[0-9]/,         // x_2 (single digit subscript)
  /[A-Za-z0-9]+\^\{[^}]+\}/,    // x^{exponent}
  /[A-Za-z0-9]+\^[0-9]/,        // x^2 (single digit exponent)
  /\\times/,                     // multiplication
  /\\div/,                       // division
  /\\pm/,                        // plus-minus
  /\\pi/,                        // pi
  /\\alpha/,                     // alpha
  /\\beta/,                      // beta
  /\\theta/,                     // theta
  /\\gamma/,                     // gamma
  /\\delta/,                     // delta
  /\\sum/,                       // summation
  /\\int/,                       // integral
  /\\infty/,                     // infinity
  /\\leq/,                       // less than or equal
  /\\geq/,                       // greater than or equal
  /\\neq/,                       // not equal
  /\\approx/,                    // approximately
  /\\rightarrow/,                // right arrow
  /\\degree/,                    // degree symbol
];

// Check if text contains any math expressions
export function containsMath(text) {
  if (!text) return false;
  
  // Check for explicit delimiters
  if (
    text.includes('\\(') ||
    text.includes('\\[') ||
    /\$[^$]+\$/.test(text)
  ) {
    return true;
  }
  
  // Check for bare LaTeX patterns
  return LATEX_PATTERNS.some(pattern => pattern.test(text));
}

// Wrap bare LaTeX expressions with delimiters so KaTeX can render them
function wrapBareLaTeX(text) {
  if (!text) return text;
  
  let result = text;
  
  // Patterns to find and wrap with \( \)
  const wrappablePatterns = [
    // Fractions: \frac{a}{b}
    /(\\frac\{[^}]+\}\{[^}]+\})/g,
    // Square roots: \sqrt{x} or \sqrt[n]{x}
    /(\\sqrt(?:\[[^\]]+\])?\{[^}]+\})/g,
    // Subscripts with braces: X_{abc}
    /([A-Za-z]+_\{[^}]+\})/g,
    // Superscripts with braces: X^{abc}
    /([A-Za-z]+\^\{[^}]+\})/g,
    // Chemical formulas like CO_2, H_2O (letter followed by _digit)
    /\b([A-Z][a-z]?_[0-9]+)/g,
    // Simple exponents like x^2, 10^3
    /\b([a-z0-9]+\^[0-9]+)\b/gi,
    // Greek letters
    /(\\(?:alpha|beta|gamma|delta|epsilon|theta|lambda|mu|pi|sigma|omega|Alpha|Beta|Gamma|Delta|Theta|Lambda|Pi|Sigma|Omega))/g,
    // Operators that should be rendered
    /(\\(?:times|div|pm|mp|cdot|leq|geq|neq|approx|equiv|infty|sum|prod|int|rightarrow|leftarrow|degree))/g,
  ];
  
  for (const pattern of wrappablePatterns) {
    result = result.replace(pattern, (match) => {
      // Don't double-wrap if already in delimiters
      const before = result.substring(0, result.indexOf(match));
      const openDelimiters = (before.match(/\\\(/g) || []).length;
      const closeDelimiters = (before.match(/\\\)/g) || []).length;
      if (openDelimiters > closeDelimiters) {
        return match; // Already inside delimiters
      }
      return `\\(${match}\\)`;
    });
  }
  
  return result;
}

// Render a single math expression safely
function SafeMath({ math, block = false }) {
  try {
    if (block) {
      return <BlockMath math={math} />;
    }
    return <InlineMath math={math} />;
  } catch (error) {
    // If KaTeX fails to parse, show the raw text
    console.warn('KaTeX parse error:', error.message, 'for:', math);
    return <code className="text-orange-600 bg-orange-50 px-1 rounded text-sm">{math}</code>;
  }
}

// Parse and render text with mixed content (text + math)
export function MathText({ children, className = '' }) {
  const text = typeof children === 'string' ? children : '';
  
  if (!text) return null;
  
  // First, wrap any bare LaTeX patterns
  const processedText = wrapBareLaTeX(text);
  
  // If no math detected after processing, return plain text
  if (!containsMath(processedText)) {
    return <span className={className}>{text}</span>;
  }
  
  // Process the text to find and render math expressions
  const elements = [];
  let lastIndex = 0;
  let key = 0;
  
  // Combined pattern for all math delimiters
  // Order matters: check block math first, then inline
  // IMPORTANT: For single $, require at least one LaTeX-like character to avoid matching currency ($40, $25)
  // Currency pattern: $ followed by digits only (no letters or LaTeX commands)
  const combinedPattern = /\\\[([\s\S]*?)\\\]|\$\$([\s\S]*?)\$\$|\\\(([\s\S]*?)\\\)|\$(?!\d+(?:\.\d+)?(?:\s|$|,|;|!|\?|\.))([^$]*?[\\^_{}].*?)\$/g;
  
  let match;
  while ((match = combinedPattern.exec(processedText)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      elements.push(
        <span key={key++}>{processedText.slice(lastIndex, match.index)}</span>
      );
    }
    
    // Determine which group matched and if it's block or inline
    let mathContent = null;
    let isBlock = false;
    
    if (match[1] !== undefined) {
      // \[ ... \] - block math
      mathContent = match[1];
      isBlock = true;
    } else if (match[2] !== undefined) {
      // $$ ... $$ - block math
      mathContent = match[2];
      isBlock = true;
    } else if (match[3] !== undefined) {
      // \( ... \) - inline math
      mathContent = match[3];
    } else if (match[4] !== undefined) {
      // $ ... $ - inline math
      mathContent = match[4];
    }
    
    if (mathContent !== null) {
      elements.push(
        <SafeMath key={key++} math={mathContent.trim()} block={isBlock} />
      );
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text after last match
  if (lastIndex < processedText.length) {
    elements.push(<span key={key++}>{processedText.slice(lastIndex)}</span>);
  }
  
  return <span className={className}>{elements}</span>;
}

// Render formatted text with bold, italic, and math support
export function FormattedMathText({ text, className = '' }) {
  if (!text) return null;
  
  // Process text to handle markdown formatting
  // Order: bold (**text**), then italic (*text*)
  
  // First, temporarily replace bold markers to avoid confusion with italic
  let processedText = text;
  const boldMatches = [];
  processedText = processedText.replace(/\*\*([^*]+)\*\*/g, (match, content, offset) => {
    const placeholder = `__BOLD_${boldMatches.length}__`;
    boldMatches.push(content);
    return placeholder;
  });
  
  // Now handle single asterisks for italic
  const parts = [];
  let lastIndex = 0;
  const italicRegex = /\*([^*]+)\*/g;
  let match;
  
  while ((match = italicRegex.exec(processedText)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: processedText.slice(lastIndex, match.index) });
    }
    // Add italic content
    parts.push({ type: 'italic', content: match[1] });
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < processedText.length) {
    parts.push({ type: 'text', content: processedText.slice(lastIndex) });
  }
  
  // If no parts, use original processed text
  if (parts.length === 0) {
    parts.push({ type: 'text', content: processedText });
  }
  
  // Now render, restoring bold markers
  const restoreBold = (content) => {
    if (!content) return content;
    let result = content;
    boldMatches.forEach((boldContent, index) => {
      result = result.replace(`__BOLD_${index}__`, `**${boldContent}**`);
    });
    return result;
  };
  
  return (
    <span className={className}>
      {parts.map((part, i) => {
        const restoredContent = restoreBold(part.content);
        
        if (part.type === 'italic') {
          // Italic text - render with emphasis
          return (
            <em key={i} className="italic text-emerald-700 font-medium">
              <MathText>{restoredContent}</MathText>
            </em>
          );
        }
        
        // Regular text - check for bold markers
        const boldParts = restoredContent.split(/(\*\*[^*]+\*\*)/g);
        
        return (
          <span key={i}>
            {boldParts.map((boldPart, j) => {
              if (boldPart.startsWith('**') && boldPart.endsWith('**')) {
                // Bold text
                const boldContent = boldPart.slice(2, -2);
                return (
                  <strong key={j} className="font-semibold text-gray-900">
                    <MathText>{boldContent}</MathText>
                  </strong>
                );
              }
              // Regular text - check for math
              return <MathText key={j}>{boldPart}</MathText>;
            })}
          </span>
        );
      })}
    </span>
  );
}

export default MathText;
